/**
 * safeFetch — fetch wrapper that falls back to XMLHttpRequest when a browser
 * extension (e.g. Papago / Naver Whale 번역기 / Google Translate) intercepts
 * the request and throws:
 *   "Failed to execute 'postMessage' on 'Window': Request object could not be cloned."
 *
 * The extension hooks fetch and tries to relay the Request via window.postMessage,
 * but Request contains non-cloneable properties (AbortSignal/ReadableStream).
 * Using XHR avoids the hook entirely.
 */
const isExtensionPostMessageError = (err) => {
  if (!err) return false;
  const msg = String(err.message || err);
  return msg.includes("postMessage") && msg.includes("could not be cloned");
};

const xhrFetch = (url, options = {}) =>
  new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest();
      const method = (options.method || "GET").toUpperCase();
      xhr.open(method, url, true);

      // headers
      const headers = options.headers || {};
      if (headers instanceof Headers) {
        headers.forEach((v, k) => xhr.setRequestHeader(k, v));
      } else if (Array.isArray(headers)) {
        headers.forEach(([k, v]) => xhr.setRequestHeader(k, v));
      } else {
        Object.keys(headers).forEach((k) => xhr.setRequestHeader(k, headers[k]));
      }

      xhr.onload = () => {
        const status = xhr.status;
        const text = xhr.responseText;
        resolve({
          ok: status >= 200 && status < 300,
          status,
          statusText: xhr.statusText,
          text: async () => text,
          json: async () => {
            try { return JSON.parse(text); } catch { return null; }
          },
        });
      };
      xhr.onerror = () => reject(new TypeError("Network request failed"));
      xhr.ontimeout = () => reject(new TypeError("Network request timed out"));
      xhr.send(options.body || null);
    } catch (e) {
      reject(e);
    }
  });

export const safeFetch = async (url, options = {}) => {
  try {
    return await fetch(url, options);
  } catch (err) {
    if (isExtensionPostMessageError(err)) {
      // Browser extension blocked native fetch — fall back to XHR
      // eslint-disable-next-line no-console
      console.warn("[safeFetch] fetch blocked by extension, falling back to XHR:", url);
      return xhrFetch(url, options);
    }
    throw err;
  }
};

const parseBody = (text) => {
  if (!text) return {};
  try { return JSON.parse(text); } catch { return { detail: text }; }
};

/**
 * safeFetchJson — issues a request and safely returns { ok, status, data }.
 * Robust against mobile in-app browsers (KakaoTalk/Naver) and translation
 * extensions that hook fetch and consume the response body before our code,
 * which throws "body stream already read" on .text()/.json().
 * In that case (or on the postMessage clone error) it transparently retries via XHR.
 */
export const safeFetchJson = async (url, options = {}) => {
  try {
    const res = await fetch(url, options);
    try {
      const text = await res.text();
      return { ok: res.ok, status: res.status, data: parseBody(text) };
    } catch (readErr) {
      // Body stream was already consumed by a webview/extension hook → retry via XHR
      // eslint-disable-next-line no-console
      console.warn("[safeFetchJson] body stream unreadable, retrying via XHR:", url);
      const xhrRes = await xhrFetch(url, options);
      const text = await xhrRes.text();
      return { ok: xhrRes.ok, status: xhrRes.status, data: parseBody(text) };
    }
  } catch (err) {
    if (isExtensionPostMessageError(err)) {
      const xhrRes = await xhrFetch(url, options);
      const text = await xhrRes.text();
      return { ok: xhrRes.ok, status: xhrRes.status, data: parseBody(text) };
    }
    throw err;
  }
};

export default safeFetch;
